/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react/display-name */
/* eslint-disable no-console */
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import React, { useEffect, useState, useRef } from 'react';

import MessagingService from '../../services/MessagingService';
import {
  mergeArrayOfObjects,
  mergeAndOverrideArrayOfObjects
} from '../../utils/mergeArrays';
import { ChimeAPI, Persistence } from '../../api/ChimeAPI';
import { useIdentityService } from '../IdentityProvider';
import {
  useErrorHandler,
  useInternalSession,
  useMember
} from '../SessionProvider';
import ImageService from '../../services/ImageService';
import { usePush } from '../PushProvider';
import { isSupportedFcm, updatePushToken } from '../../utils/firebase.utils';
import { signOut } from 'next-auth/react';
import { ChannelMessagePersistenceType } from '@aws-sdk/client-chime-sdk-messaging';
import { getChannelWithLatestMessage } from '../../utils/chime.utils';
import { useAppState } from '../AppStateProvider';

const MessagingProvider = ({
  children,
  chimeId,
  ChatMessagingServiceContext,
  ImageServiceContext,
  ChatMessagingState,
  ChatChannelState,
  loadChannels
}) => {
  const { identityClient, credentialProvider } = useIdentityService();
  const { member } = useMember();
  const { status, data } = useInternalSession();
  const { createProxy, onError } = useErrorHandler();
  const isAuthenticated = status === 'authenticated';
  const soundRef = useRef(new Audio(process.env.NEXT_PUBLIC_MESSAGE_SOUND));
  const chimeApi = createProxy(
    new ChimeAPI(chimeId, credentialProvider, identityClient)
  );
  const [messagingService] = useState(
    () =>
      new MessagingService(chimeApi, onError, () => {
        // Websocketリクエストタイムアウトエラーフラグをtrueにする
        setWsRequestTimeout(true);
      })
  );
  const [imageService] = useState(() =>
    createProxy(new ImageService(data.idToken))
  );
  // Channel related
  const [activeChannel, setActiveChannel] = useState({});
  const [activeView, setActiveView] = useState('User');
  const [activeSubChannel, setActiveSubChannel] = useState({});
  const [activeChannelMemberships, setActiveChannelMemberships] = useState([]);
  const [
    activeChannelMembershipsWithPresence,
    setActiveChannelMembershipsWithPresence
  ] = useState([]);
  const activeSubChannelRef = useRef(activeSubChannel.ChannelArn);
  const activeChannelRef = useRef(activeChannel.ChannelArn);
  const currentVisible = useRef(document.visibilityState === 'visible');
  const [channelList, setChannelList] = useState([]);
  const [channelListModerator, setChannelListModerator] = useState([]);
  const [subChannelList, setSubChannelList] = useState([]);
  const [subChannelIds, setSubChannelIds] = useState([]);
  const [unreadChannels, setUnreadChannels] = useState([]);
  const [userMatchList, setUserMatchList] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const { hasNewMessage, setHasNewMessage } = useAppState();
  const unreadChannelsListRef = useRef(unreadChannels);
  const hasMembership =
    activeChannelMemberships
      .map((m) => m.Member.Arn)
      .indexOf(chimeApi.createMemberArn()) > -1;
  const [isHideListChecked, setIsHideListChecked] = useState(false);
  // Messages
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState();
  const isAuthenticatedRef = useRef(isAuthenticated);
  const messagesRef = useRef(messages);
  const channelListRef = useRef(channelList);
  const [moderatedChannel, setModeratedChannel] = useState('');
  const channelListModeratorRef = useRef(channelListModerator);
  const subChannelListRef = useRef(subChannelList);
  const activeChannelMembershipsRef = useRef(activeChannelMemberships);
  const activeChannelMembershipsWithPresenceRef = useRef(
    activeChannelMembershipsWithPresence
  );
  const [channelMessageToken, setChannelMessageToken] = useState('');
  const channelMessageTokenRef = useRef(channelMessageToken);
  // Meeting
  const [meetingInfo, setMeetingInfo] = useState('');
  const { isPushSupported, token, hasNotificationPermission } = usePush();
  const [channelDetailCount, setChannelDetailCount] = useState(0);
  const [wsRequestTimeout, setWsRequestTimeout] = useState(false);
  const [lastReadTimestamp, setLastReadTimestamp] = useState(0);

  useEffect(() => {
    if (hasNotificationPermission && isPushSupported && token) {
      chimeApi.registerAppInstanceUserEndpoint(token);
    }
  }, [isPushSupported, token, hasNotificationPermission, chimeApi]);

  useEffect(() => {
    // ChannelDetailのカウントが50件の時に、すべてのチャネルを取得する。
    // 理由：50件以上あっても、50件しか返していないので、それ以上あるかどうか判定できないから。
    if (loadChannels && channelDetailCount === 50) {
      getAllChannels();
    }
  }, [channelDetailCount]);

  useEffect(() => {
    // Websocketリクエストタイムアウトエラーがあった場合、手動ですべてのチャネルを取得する。
    if (wsRequestTimeout) {
      getAllChannels();
    }
  }, [wsRequestTimeout]);

  const getAllChannels = () => {
    identityClient.getListChannels().then((channels) => {
      if (channels?.length) {
        Promise.all(
          channels.map((channel) => {
            // 現在のチャネル一覧に該当するチャネルを探す
            const appropriateChannel = channelListRef.current.find(
              (currentChannel) =>
                currentChannel.ChannelArn === channel.ChannelArn
            );
            // 該当するチャネルがある場合
            if (appropriateChannel) {
              // 最新メッセージがある AND LastMessageTimestampが古い場合に最新メッセージを更新する
              if (
                appropriateChannel.LatestMessage &&
                appropriateChannel.LastMessageTimestamp.getTime() <
                  (channel.LastMessageTimestamp?.getTime() || 0)
              ) {
                return getChannelWithLatestMessage(channel, chimeApi);
              } else {
                return Promise.resolve(appropriateChannel);
              }
            } else {
              return Promise.resolve(channel);
            }
          })
        ).then((newChannelList) => {
          setChannelList(newChannelList);
        });
      }
    });
  };

  useEffect(() => {
    if (isSupportedFcm()) {
      // Notificationを使えないブラウザではエラーになるのでif文をネストしている
      if (Notification.permission === 'granted') {
        // tokenの更新チェックを行う
        updatePushToken(true, identityClient);
      }
    }
  }, [chimeId]);

  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
    messagesRef.current = messages;
    activeChannelRef.current = activeChannel;
    activeSubChannelRef.current = activeSubChannel;
    channelListRef.current = channelList;
    channelListModeratorRef.current = channelListModerator;
    subChannelListRef.current = subChannelList;
    unreadChannelsListRef.current = unreadChannels;
    activeChannelMembershipsRef.current = activeChannelMemberships;
    activeChannelMembershipsWithPresenceRef.current =
      activeChannelMembershipsWithPresence;
    channelMessageTokenRef.current = channelMessageToken;
  }, [
    isAuthenticated,
    messages,
    activeChannel,
    activeSubChannel,
    channelList,
    channelListModerator,
    subChannelList,
    unreadChannels,
    activeChannelMemberships,
    activeChannelMembershipsWithPresence,
    channelMessageToken
  ]);

  // Messaging service initiator
  useEffect(() => {
    if (!isAuthenticated) return;

    // Start messaging service
    messagingService.connect();

    // return () => {
    //   messagingService.close();
    // };
  }, [isAuthenticated]);

  // タブがアクティブか検知する
  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const handleVisibilityChange = () => {
    currentVisible.current = document.visibilityState === 'visible';

    // バックグラウンドから復帰、かつ、チャネル情報がある時に既読取得と、更新をする
    if (currentVisible.current && activeChannelRef.current.ChannelArn) {
      console.log('back to forward. and exist active channel');
      getLastReadTime();
      updateLastReadTime();
    }
  };

  const updateLastReadTime = async () => {
    await identityClient.updateLastReadTimestamp({
      isLineUser: member.userType !== 'estate',
      companyId: activeChannelRef.current.Metadata.CompanyId,
      matchId: activeChannelRef.current.matchId
    });
  };

  const getLastReadTime = async () => {
    const _lastReadTime = await identityClient.getLastReadTimestamp({
      isLineUser: member.userType !== 'estate',
      companyId: activeChannelRef.current.Metadata.CompanyId,
      matchId: activeChannelRef.current.matchId
    });
    if (_lastReadTime) {
      setLastReadTimestamp(_lastReadTime);
    }
  };

  // メッセージ受信時かつ会員の場合に、非表示のチャンネルを表示に切り替え最新データで描画させる
  const updateChannelVisible = async (companyId, channelArn) => {
    companyId &&
      identityClient.getUserMatchList(companyId).then((userMatchList) => {
        userMatchList.find((userMatch) => {
          if (
            userMatch.messageChannelArn === channelArn &&
            userMatch.isHideChannel === true
          ) {
            identityClient
              .updateChannelVisible(companyId, userMatch.matchId, false)
              .then(() => {
                // 更新後のUserMatchで再描画する
                identityClient
                  .getUserMatchList(companyId)
                  .then((userMatchList) => {
                    setUserMatchList(userMatchList);
                  });
              });
          }
        });
      });
  };

  const processChannelMessage = async (message) => {
    setNewMessage(message);
    setMessages((prevMessage) => {
      let isDuplicate = false;
      prevMessage.forEach((m, i, self) => {
        if ((m.response?.MessageId || m.MessageId) === message.MessageId) {
          console.log('Duplicate message found', message);
          isDuplicate = true;
          self[i] = message;
        }
      });

      return !isDuplicate && message.Persistence === Persistence.PERSISTENT
        ? [...prevMessage, message]
        : [...prevMessage];
    });
  };

  const addUpdateDisabledMessage = async () => {
    const newMessage = {
      OldMessageUpdateDisabled: true
    };
    setMessages([...messagesRef.current, newMessage]);
  };

  const messagesProcessor = async (message) => {
    const messageType = message?.headers['x-amz-chime-event-type'];
    const record = ((record) =>
      record && typeof record.Metadata === 'string'
        ? { ...record, Metadata: JSON.parse(record.Metadata || '{}') }
        : record)(JSON.parse(message?.payload));
    console.log('Incoming Message', message);
    switch (messageType) {
      case 'SESSION_ESTABLISHED':
        console.log('WebSocketに接続しました。');
        // ChannelDetailのカウントをリセットする
        setChannelDetailCount(0);
        break;
      case 'CHANNEL_DETAILS':
        const channelMetadata = JSON.parse(record.Channel?.Metadata || '{}');
        const latestMessage = record.ChannelMessages?.[0];
        const latestMessageMetadata = JSON.parse(
          latestMessage?.Metadata || '{}'
        );
        if (!channelMetadata?.isHidden) {
          setChannelList(
            mergeArrayOfObjects(
              channelListRef.current,
              [
                {
                  ...record.Channel,
                  Metadata: channelMetadata,
                  LastMessageTimestamp:
                    record.Channel?.LastMessageTimestamp &&
                    new Date(record.Channel.LastMessageTimestamp),
                  LatestMessage: latestMessage && {
                    content: latestMessage?.Content,
                    userId: latestMessageMetadata.UserId,
                    type: latestMessageMetadata.Type,
                    isAutomated: !!latestMessageMetadata.AutomatedMsg,
                    direction:
                      latestMessage?.Sender?.Arn === chimeApi.createMemberArn()
                        ? 1
                        : 0
                  }
                }
              ],
              'ChannelArn'
            )
          );
        }
        // ChannelDetailのカウントをプラスする
        setChannelDetailCount((c) => c + 1);
        break;
      // Channel Messages
      case 'CREATE_CHANNEL_MESSAGE':
        if (
          record.Persistence === ChannelMessagePersistenceType.NON_PERSISTENT
        ) {
          const content = JSON.parse(record.Content || '{}');
          if (content.Type === 'ForceLogout' && content.UserId === member.id) {
            signOut({ callbackUrl: '/login/hub' });
          } else if (
            // 下記条件の場合に、既読通知を受け取る
            // 対象のチャンネルを開いている
            // 送信者が自分以外
            // 会員とスタッフ間ではない
            content.Type === 'LastReadTime' &&
            content.userId !== member.userId &&
            !(
              content.userId.includes('kaiin_') &&
              member.userId.includes('kaiin_')
            ) &&
            content.lastReadTimestamp &&
            activeChannelRef.current.ChannelArn === record?.ChannelArn
          ) {
            // 既読通知を受信したので、メッセージの既読を更新
            console.log('incoming last read notice');

            setLastReadTimestamp(content.lastReadTimestamp);
          }
          break;
        }

        if (record.Sender.Arn !== chimeApi.createMemberArn()) {
          soundRef.current.play();
          setHasNewMessage(true);
        }
        // 通知の来たチャンネルが非表示だったら表示に更新する
        updateChannelVisible(member.companyId, record.ChannelArn);
      case 'REDACT_CHANNEL_MESSAGE':
      case 'UPDATE_CHANNEL_MESSAGE':
      case 'DELETE_CHANNEL_MESSAGE':
      case 'DENIED_CREATE_CHANNEL_MESSAGE':
      case 'FAILED_CREATE_CHANNEL_MESSAGE':
      case 'DENIED_UPDATE_CHANNEL_MESSAGE':
      case 'FAILED_UPDATE_CHANNEL_MESSAGE':
      case 'PENDING_CREATE_CHANNEL_MESSAGE':
      case 'PENDING_UPDATE_CHANNEL_MESSAGE':
        // Process ChannelMessage
        if (record.Metadata) {
          const metadata = record.Metadata;
          if (
            metadata.isMeetingInfo &&
            record.Sender.Arn !== chimeApi.createMemberArn()
          ) {
            const meetingInfo = JSON.parse(record.Content);
            setMeetingInfo(meetingInfo);
            break;
          }
        }

        // Process channel message
        if (
          activeChannelRef.current.ChannelArn === record?.ChannelArn &&
          activeChannelRef.current.SubChannelId === record.SubChannelId
        ) {
          // フォア かつ 開いているチャンネルからのメッセージの場合は既読更新
          if (currentVisible.current) {
            updateLastReadTime();
          }
          processChannelMessage(record);
        }
        const channel = channelListRef.current.find(
          (channel) => channel.ChannelArn === record?.ChannelArn
        );
        if (channel) {
          const metadata = record.Metadata || {};
          setChannelList(
            mergeArrayOfObjects(
              channelListRef.current,
              [
                {
                  ...channel,
                  ...(channel.LastMessageTimestamp <=
                  new Date(record.CreatedTimestamp)
                    ? {
                        LatestMessage: {
                          content: record.Content,
                          userId: metadata.UserId,
                          direction:
                            record.Sender.Arn === chimeApi.createMemberArn()
                              ? 1
                              : 0,
                          metadata,
                          type: metadata.Type,
                          isAutomated: !!metadata.AutomatedMsg
                        },
                        LastMessageTimestamp: new Date(record.CreatedTimestamp)
                      }
                    : {})
                }
              ],
              'ChannelArn'
            )
          );
        }
        break;
      // Channels actions
      case 'CREATE_CHANNEL':
      case 'UPDATE_CHANNEL':
        const updatedChannelList = channelListRef.current.map((c) =>
          c.ChannelArn === record.ChannelArn ? c : record
        );
        setChannelList(updatedChannelList);
        setActiveChannel(record);
        break;
      case 'DELETE_CHANNEL': {
        setChannelList(
          channelListRef.current.filter(
            (chRef) => chRef.ChannelArn !== record.ChannelArn
          )
        );
        if (activeChannelRef.current.ChannelArn === record.ChannelArn) {
          setActiveChannel({});
        }
        break;
      }
      // Channel Memberships
      case 'CREATE_CHANNEL_MEMBERSHIP':
        {
          const newChannel = await chimeApi.describeChannel(record.ChannelArn);
          if (!newChannel) {
            return;
          }
          if (record.SubChannelId) {
            newChannel.SubChannelId = record.SubChannelId;
            subChannelIds.push(record.SubChannelId);
          }

          if (newChannel.Metadata) {
            const metadata = newChannel.Metadata;
            if (metadata.isHidden) return;
          }
          if (record.ChannelArn === activeChannelRef.current.ChannelArn) {
            const newMessages = await chimeApi.listChannelMessages(
              newChannel.ChannelArn,
              newChannel.SubChannelId
            );
            setMessages(newMessages.Messages);
            setChannelMessageToken(newMessages.NextToken);
            setActiveChannel(newChannel);
          }
          setChannelList(
            mergeArrayOfObjects(
              channelListRef.current,
              [await getChannelWithLatestMessage(newChannel, chimeApi)],
              'ChannelArn'
            )
          );
        }
        break;
      case 'UPDATE_CHANNEL_MEMBERSHIP':
        if (
          `${process.env.NEXT_PUBLIC_ARN_INSTANCE}/user/${chimeApi._profile}` !==
          record?.InvitedBy.Arn
        ) {
          const channel = await chimeApi.describeChannel(record?.ChannelArn);
          if (record.SubChannelId) {
            channel.SubChannelId = record.SubChannelId;
            if (activeChannelRef.current.ChannelArn === record?.ChannelArn) {
              addUpdateDisabledMessage(record.ChannelArn);
              setActiveChannel(channel);
            }
          }
          setChannelList(
            mergeArrayOfObjects(channelListRef.current, [channel], 'ChannelArn')
          );
        }
        break;
      case 'DELETE_CHANNEL_MEMBERSHIP':
        // You are removed
        if (record.Member.Arn.includes(member.userId)) {
          setChannelList(
            channelListRef.current.filter(
              (chRef) => chRef.ChannelArn !== record.ChannelArn
            )
          );
          if (record.SubChannelId) {
            const foundIndex = subChannelIds.findIndex(
              (ori) => ori === record.SubChannelId
            );
            subChannelIds.splice(foundIndex, 1);
            setSubChannelIds(subChannelIds);
            const newSubChannelList = subChannelListRef.current.filter(
              (ori) => ori.SubChannelId !== record.SubChannelId
            );
            setSubChannelList(newSubChannelList);
          }
          if (
            activeChannelRef.current.ChannelArn === record.ChannelArn &&
            activeChannelRef.current.SubChannelId === record.SubChannelId
          ) {
            setActiveChannel({});
          }
        } else {
          // Someone else is removed
          const updatedMemberships = activeChannelMembershipsRef.current.filter(
            (m) => m.Member.Arn !== record.Member.Arn
          );
          setActiveChannelMemberships(updatedMemberships);
        }
        break;
      default:
        console.log(`Unexpected message type! ${messageType}`);
    }
  };

  // Subscribe to MessagingService for updates
  useEffect(() => {
    if (!isAuthenticated) return;
    messagingService.subscribeToMessageUpdate(messagesProcessor);
    return () => {
      messagingService.unsubscribeFromMessageUpdate(messagesProcessor);
    };
  }, [messagingService, isAuthenticated]);

  // Providers values
  const messageStateValue = {
    messages,
    messagesRef,
    setMessages,
    chimeApi,
    newMessage,
    setNewMessage,
    lastReadTimestamp,
    setLastReadTimestamp
  };
  const channelStateValue = {
    channelList,
    channelListModerator,
    subChannelList,
    subChannelIds,
    activeChannel,
    activeView,
    activeSubChannel,
    activeChannelRef,
    activeSubChannelRef,
    channelListRef,
    channelListModeratorRef,
    subChannelListRef,
    unreadChannels,
    activeChannelMemberships,
    activeChannelMembershipsWithPresence,
    hasMembership,
    channelMessageToken,
    channelMessageTokenRef,
    meetingInfo,
    isOpen,
    isHideListChecked,
    userMatchList,
    setActiveChannel,
    setActiveView,
    setActiveSubChannel,
    setActiveChannelMemberships,
    setActiveChannelMembershipsWithPresence,
    setChannelMessageToken,
    setChannelList,
    setChannelListModerator,
    setSubChannelList,
    setSubChannelIds,
    setUnreadChannels,
    setMeetingInfo,
    moderatedChannel,
    setModeratedChannel,
    setIsOpen,
    setIsHideListChecked,
    setUserMatchList
  };
  return (
    <ChatMessagingServiceContext.Provider value={messagingService}>
      <ImageServiceContext.Provider value={imageService}>
        <ChatChannelState.Provider value={channelStateValue}>
          <ChatMessagingState.Provider value={messageStateValue}>
            {children}
          </ChatMessagingState.Provider>
        </ChatChannelState.Provider>
      </ImageServiceContext.Provider>
    </ChatMessagingServiceContext.Provider>
  );
};

export { MessagingProvider };
